import { createPortal } from "react-dom";
import { Clock, CheckCircle, AlertCircle } from "lucide-react";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    campaignName: string;
    templateName: string;
    campaignType: "Marketing" | "Renovación" | "Dinámica" | "Calendar";
}

const PendingApprovalModal: React.FC<Props> = ({
    isOpen,
    onClose,
    templateName,
}) => {
    if (!isOpen) return null;

    const modal = (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center">
            {/* Backdrop - sin onClick para que solo cierre con el botón */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
                {/* Header con amarillo suave */}
                <div className="bg-amber-50 border-b border-amber-100 px-6 py-5">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                            <Clock className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                Campaña Creada
                            </h2>
                            <p className="text-amber-700 text-sm">
                                Pendiente de activación
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body - Timeline vertical */}
                <div className="px-6 py-6">
                    <div className="relative">
                        {/* Línea vertical conectora */}
                        <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gray-200"></div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4 relative">
                                <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0 z-10 border-2 border-white shadow-sm">
                                    <AlertCircle className="w-4 h-4 text-amber-600" />
                                </div>
                                <div className="pt-0.5">
                                    <h3 className="font-semibold text-gray-900">Nueva plantilla pendiente de aprobación</h3>
                                    <p className="text-gray-600 text-sm mt-1">
                                        La plantilla <span className="font-medium text-gray-800">"{templateName}"</span> fue creada y enviada a Meta para su revisión.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 relative">
                                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 z-10 border-2 border-white shadow-sm">
                                    <Clock className="w-4 h-4 text-blue-600" />
                                </div>
                                <div className="pt-0.5">
                                    <h3 className="font-semibold text-gray-900">Tiempo de aprobación</h3>
                                    <p className="text-gray-600 text-sm mt-1">
                                        El proceso de revisión puede tomar <span className="font-semibold text-blue-600">hasta 24 horas</span>.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 relative">
                                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0 z-10 border-2 border-white shadow-sm">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                </div>
                                <div className="pt-0.5">
                                    <h3 className="font-semibold text-gray-900">Activación automática</h3>
                                    <p className="text-gray-600 text-sm mt-1">
                                        Una vez que Meta apruebe la plantilla, tu campaña se activará automáticamente y comenzará a enviar mensajes.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );

    return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
};

export default PendingApprovalModal;
